import { Auth } from "aws-amplify";

export const COGNITO_GROUP_ADMIN = "Admin";
export const COGNITO_GROUP_BUSINESS_DEVELOPMENT = "business-development";

/** Paths business-development users may open (and see in nav). */
export const BUSINESS_DEVELOPMENT_ALLOWED_PATHS = ["/create-quicklink"];

function groupsFromPayload(payload) {
  const groups = payload?.["cognito:groups"];
  return Array.isArray(groups) ? groups.map(String) : [];
}

function refreshSession(user) {
  return new Promise((resolve, reject) => {
    const current = user?.signInUserSession;
    const refreshToken = current?.getRefreshToken?.();
    if (!user?.refreshSession || !refreshToken) {
      resolve(current || null);
      return;
    }
    user.refreshSession(refreshToken, (err, session) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(session);
    });
  });
}

/**
 * Read Cognito groups from the current session (access + id token).
 * Pass forceRefresh only after sign-in so newly assigned groups appear;
 * do not call it on every render or on tokenRefresh (that rate-limits Cognito).
 */
export async function getCognitoGroups({ forceRefresh = false } = {}) {
  try {
    const user = await Auth.currentAuthenticatedUser({
      bypassCache: forceRefresh,
    });
    let session = user?.signInUserSession;
    if (forceRefresh) {
      try {
        session = (await refreshSession(user)) || session;
      } catch {
        // keep existing session (e.g. TooManyRequestsException)
      }
    }
    const fromAccess = groupsFromPayload(session?.getAccessToken?.()?.payload);
    const fromId = groupsFromPayload(session?.getIdToken?.()?.payload);
    return fromAccess.length ? fromAccess : fromId;
  } catch {
    return [];
  }
}

export function hasCognitoGroup(groups, groupName) {
  const want = String(groupName || "").toLowerCase();
  return (groups || []).some((g) => String(g).toLowerCase() === want);
}

/**
 * business-development only (no Admin) → restricted to Create QuickLink.
 * Admin (with or without BD) keeps full admin access.
 */
export function isBusinessDevelopmentOnly(groups = []) {
  const isBd = hasCognitoGroup(groups, COGNITO_GROUP_BUSINESS_DEVELOPMENT);
  const isAdmin = hasCognitoGroup(groups, COGNITO_GROUP_ADMIN);
  return isBd && !isAdmin;
}

export function isPathAllowedForBusinessDevelopment(pathname = "") {
  const path = String(pathname || "");
  return BUSINESS_DEVELOPMENT_ALLOWED_PATHS.some(
    (allowed) => path === allowed || path.startsWith(`${allowed}/`)
  );
}
